/**
 * Every visible date goes through these helpers so the whole UI shows the
 * same Dutch numeric format.
 */
export const DATE_LOCALE = "nl-NL";

const parse = (value: string | Date | null | undefined): Date | null => {
  if (value == null || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const numeric = new Intl.DateTimeFormat(DATE_LOCALE, {
  day: "numeric",
  month: "numeric",
  year: "numeric",
});

const long = new Intl.DateTimeFormat(DATE_LOCALE, {
  day: "numeric",
  month: "long",
  year: "numeric",
});

const dateTime = new Intl.DateTimeFormat(DATE_LOCALE, {
  day: "numeric",
  month: "numeric",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

/**
 * Numeric Dutch date, e.g. `7-9-2026`. Accepts an ISO date (`YYYY-MM-DD`),
 * an ISO datetime or a `Date`; returns an empty string for empty/invalid input.
 */
export const formatDate = (value: string | Date | null | undefined): string => {
  const date = parse(value);
  return date ? numeric.format(date) : "";
};

/** Long Dutch date, e.g. `7 september 2026`. */
export const formatDateLong = (
  value: string | Date | null | undefined,
): string => {
  const date = parse(value);
  return date ? long.format(date) : "";
};

/** Numeric Dutch date with time, e.g. `7-9-2026 14:05` (for notes). */
export const formatDateTime = (
  value: string | Date | null | undefined,
): string => {
  const date = parse(value);
  return date ? dateTime.format(date) : "";
};

/** Today as an ISO date (`YYYY-MM-DD`) in local time. */
export const todayIsoDate = (): string => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
};
